import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { message } from "antd";
import { useTranslation } from "components/use-hooks";
import { formatCheckInPositionDate } from "../helpers/check-in-position-date-helper";


const getPositionUpdatedAtText = (value, translations) => (
    translations[ "CHECK_QR.Text_PositionUpdatedAt" ].replace(
        "{ date }",
        formatCheckInPositionDate(value) || translations[ "CHECK_QR.Text_DateUnavailable" ]
    )
);

const useCheckQrPosterExport = ({ eventID, qrUrl, eventTitle, positionDate, disabled = false }) => {
    const translations = useTranslation();
    const [ operation, setOperation ] = useState(null);
    const requestRef = useRef(null);
    const mountedRef = useRef(true);
    const context = useMemo(() => ({ eventID, qrUrl, disabled }), [ eventID, qrUrl, disabled ]);
    const activeContextRef = useRef(context);

    activeContextRef.current = context;

    useLayoutEffect(() => {
        mountedRef.current = true;

        return () => {
            mountedRef.current = false;
        };
    }, []);

    const exportPoster = async (type, action) => {
        if (requestRef.current?.context === context || disabled || !qrUrl) return false;

        const request = { context, type };
        const isCurrent = () => mountedRef.current && activeContextRef.current === context;
        let cancelled = false;

        requestRef.current = request;
        setOperation(request);

        try
        {
            // The exporter starts synchronously so Print can open its window before fetching.
            const completed = await action(async () => {
                const refreshed = await positionDate.refresh({ force: true });

                if (!refreshed || !isCurrent())
                {
                    cancelled = true;
                    return null;
                }

                return {
                    url: qrUrl,
                    eventID,
                    eventTitle,
                    positionUpdatedAtText: getPositionUpdatedAtText(refreshed.value, translations),
                };
            });

            if (completed === false && !cancelled && isCurrent()) throw new Error("QR poster export failed");

            return completed;
        } catch (error)
        {
            if (isCurrent()) message.error(translations[ "CHECK_QR.Text_ExportError" ]);

            return false;
        } finally
        {
            if (requestRef.current === request)
            {
                requestRef.current = null;
                if (mountedRef.current) setOperation(null);
            }
        }
    };

    return {
        exportLoading: operation?.context === context ? operation.type : null,
        exportPoster,
        positionUpdatedAtText: getPositionUpdatedAtText(positionDate?.value, translations),
    };
};

export default useCheckQrPosterExport;
